import { Injectable, computed, signal } from '@angular/core';
import { MatchSelection } from '../../services/stage-state.service';
import {
  BrokerProfileContext,
  BrokerProfileMode,
  BrokerProfileSource,
  ProductType,
  RateDisplay,
} from './broker-profile.models';
import { StageStateService } from '../../services/stage-state.service';
import { inject } from '@angular/core';

/**
 * State for the broker-tier product profile flyout. Single-slot only.
 * Opens from Stage 04 Compare column headers (Phase 1) and later from
 * Match cards, rate-sheet rows, and grid rows (Phase 2+).
 *
 * Reference tier has its own flyout at src/app/shared/profiles/ — do not
 * merge. Cross-tier contamination is forbidden per CLAUDE.md Rule #10.
 */
@Injectable({ providedIn: 'root' })
export class BrokerProfileService {
  private readonly state = inject(StageStateService);

  private readonly _context = signal<BrokerProfileContext | null>(null);
  readonly context = this._context.asReadonly();
  readonly isOpen = computed(() => this._context() !== null);
  readonly mode = computed<BrokerProfileMode | null>(() => this._context()?.mode ?? null);

  openFromCompare(product: MatchSelection, rank: number): void {
    const cs = this.state.caseState();
    const peers = (cs.matchResults ?? []).filter(p => p.gmwbId !== product.gmwbId);
    this._context.set({
      source: 'stage04-compare',
      mode: 'case',
      productType: 'ia',
      product,
      rank,
      scenario: cs.scenario ?? null,
      peers,
    });
  }

  openFromMatch(product: MatchSelection, rank: number): void {
    const cs = this.state.caseState();
    const peers = (cs.matchResults ?? []).filter(p => p.gmwbId !== product.gmwbId);
    this._context.set({
      source: 'match-card',
      mode: 'case',
      productType: 'ia',
      product,
      rank,
      scenario: cs.scenario ?? null,
      peers,
    });
  }

  /**
   * Launch the flyout in Client-facing mode from a rate-sheet row click.
   * `product` only requires the subset of MatchSelection fields that
   * Client-facing mode renders; missing fields get safe defaults so the
   * body component doesn't NPE.
   */
  openFromRateSheet(
    product: Partial<MatchSelection> & Pick<MatchSelection, 'productid' | 'productName' | 'companyName'>,
    productType: ProductType,
    rateDisplay: RateDisplay,
  ): void {
    const full: MatchSelection = {
      gmwbId: product.gmwbId ?? 0,
      productid: product.productid,
      id: product.id,
      productName: product.productName,
      companyName: product.companyName,
      riderName: product.riderName ?? '',
      bestid: product.bestid ?? '',
      factorSingle: product.factorSingle ?? 0,
      factorJoint: product.factorJoint ?? 0,
      riderFee: product.riderFee ?? 0,
      rollupPer: product.rollupPer ?? '',
      maxRollupYears: product.maxRollupYears ?? null,
      surryr: product.surryr ?? 0,
      benifitBaseType: product.benifitBaseType ?? '',
      fit: product.fit ?? { total: 0, income: 0, fees: 0, flexibility: 0, strength: 0, rationale: [] },
      estAnnualIncome: product.estAnnualIncome ?? 0,
    };
    this._context.set({
      source: 'rate-sheet',
      mode: 'client-facing',
      productType,
      product: full,
      rank: 1,
      scenario: this.state.caseState().scenario ?? null,
      peers: [],
      rateDisplay,
    });
  }

  setMode(mode: BrokerProfileMode): void {
    const ctx = this._context();
    if (!ctx) return;
    this._context.set({ ...ctx, mode });
  }

  close(): void {
    this._context.set(null);
  }
}
